import { cx } from '@linaria/core';
import { Button } from 'components/ui_library/Button';
import { TextSmall } from 'components/Popover';
import { IconCheckThin, IconChevronDown } from 'components/Icons';
import { Tag } from 'components/Tag';
import { noop } from 'utils/fp';
import { baseTheme } from 'styles/utils';
import { Avatar } from 'components/Avatar';
import { account, userTag, orgTag } from './styles';

interface BaseAccountProps {
  type: 'user' | 'org';
  name?: string;
  subtitle?: string;
  withTag?: boolean;
  withArrow?: boolean;
  selected?: boolean;
  iconSize?: number;
}

type ButtonOrAnchorProps = React.PropsWithChildren<React.HTMLAttributes<HTMLAnchorElement>> &
  (
    | {
        as?: 'button';
      }
    | {
        as?: 'anchor';
        href?: string;
      }
  );

export type AccountProps = BaseAccountProps & ButtonOrAnchorProps;

type AccountIconProps = Pick<AccountProps, 'type' | 'name' | 'iconSize' | 'className'>;

export const AccountTag: React.FC<Pick<AccountIconProps, 'type' | 'className'>> = ({ type, className }) => (
  <Tag data-testid="account-tag" id={type} onChange={noop} className={cx(type === 'user' ? userTag : orgTag, 'tag', className)}>
    {type}
  </Tag>
);

export const AccountIcon: React.FC<AccountIconProps> = ({ name = '', type = 'user', iconSize = 18 }) => {
  return <Avatar userId={name} size={iconSize} data-testid={`${type}-icon`} />;
};

const AccountButtonWrapper: React.FC<ButtonOrAnchorProps> = ({ as, children, ...props }) => {
  if (as === 'button') {
    return (
      <Button variant="unstyled" size="md" {...props}>
        {children}
      </Button>
    );
  }

  return <a {...props}>{children}</a>;
};

export const Account: React.FC<AccountProps> = ({
  as = 'button',
  selected,
  type,
  name,
  subtitle,
  withTag,
  withArrow,
  className,
  iconSize,
  ...rest
}) => {
  return (
    <AccountButtonWrapper
      as={as}
      data-testid="account-btn"
      className={cx(account, 'account', className, withArrow && 'with-arrow', selected && 'selected')}
      {...rest}
    >
      <AccountIcon type={type} name={name} iconSize={iconSize} />
      <TextSmall className="name">
        {type === 'user' ? <strong>{name}</strong> : name}
        {subtitle && <TextSmall className="id">{subtitle}</TextSmall>}
      </TextSmall>
      {selected && <IconCheckThin size={16} color={baseTheme.light.colors.primary} />}
      {withArrow && <IconChevronDown />}
      {withTag && <AccountTag type={type} />}
    </AccountButtonWrapper>
  );
};
